import { useState, type ReactNode } from 'react';
import { Card, CardHeader, CardTitle } from '@paige/ui';
import { useFormat } from '@paige/format';
import { activityOf, type ActivityInput, type ActivityItem, type ActivityWho } from './activity';
import './insights.css';
import './account.css';

const FILTERS: { key: ActivityWho | 'all'; word: string }[] = [
  { key: 'all', word: 'Everything' },
  { key: 'customer', word: 'The company' },
  { key: 'staff', word: 'Ademero staff' },
  { key: 'paige', word: 'Paige' },
];

const SHOWN = 40;

/**
 * What happened on the account, in one list (round 2, support R2-M2): the audit stream, the product
 * events, staff notes and credit changes merged, newest first, filtered by who acted. Replaces the
 * separate Audit stream and Journey panels.
 */
export function ActivityPanel(
  props: ActivityInput & { loading?: boolean; timeZone?: string | undefined },
): ReactNode {
  const [who, setWho] = useState<ActivityWho | 'all'>('all');
  const items = activityOf(props)
    .filter((i) => who === 'all' || i.who === who)
    .slice(0, SHOWN);
  return (
    <Card id="ad-activity">
      <CardHeader>
        <CardTitle>Activity</CardTitle>
      </CardHeader>
      <WhoFilter who={who} onWho={setWho} />
      {items.length === 0 ? (
        <p className="ad-small">
          {props.loading ? 'Reading the activity…' : 'Nothing recorded here yet.'}
        </p>
      ) : (
        <ActivityList items={items} zone={props.timeZone} />
      )}
    </Card>
  );
}

function WhoFilter(x: { who: ActivityWho | 'all'; onWho: (w: ActivityWho | 'all') => void }) {
  return (
    <div className="ad-tabs" role="group" aria-label="Who acted">
      {FILTERS.map((f) => (
        <button
          key={f.key}
          type="button"
          aria-pressed={x.who === f.key}
          className="ad-tab"
          onClick={() => x.onWho(f.key)}
        >
          {f.word}
        </button>
      ))}
    </div>
  );
}

/** "Oct 3, 9:14 AM in their time (13:14 UTC)": every relative time has its absolute one (support 9). */
function absolute(at: string, zone: string | undefined): string {
  const d = new Date(at);
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  const local = d.toLocaleString('en-US', { ...opts, timeZone: zone ?? 'UTC' });
  const utc = d.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  });
  return `${local}${zone ? ' in their time' : ''} (${utc} UTC)`;
}

function ActivityList(x: { items: readonly ActivityItem[]; zone?: string | undefined }): ReactNode {
  const f = useFormat();
  return (
    <ol className="ad-activity">
      {x.items.map((i) => (
        <li key={i.key} className={`ad-activity__row ad-activity__row--${i.who}`}>
          <time dateTime={i.at} className="ad-small" title={absolute(i.at, x.zone)}>
            {f.ago(i.at)}
          </time>
          <span>
            <strong>{i.actor}</strong> {i.href ? <a href={i.href}>{i.text}</a> : i.text}
            {i.detail ? <span className="ad-small ad-activity__detail">{i.detail}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
